/**
 * buildLocalizableMessage
 *
 * @description
 * Encapsulates type-coupling between a translation function and its translation options at the call site.
 * Use this to avoid passing around both a translation function and its translation options.
 * The return value of this function is a function which only requires a locale to be turned into a string.
 *
 * @example
 * const translationFunction = (options: { name: string }, _locale: string) => `Hello ${options.name}`
 * const message = buildLocalizableMessage(translationFunction, { name: 'Waldo' })
 * message('en') // 'Hello Waldo'
 *
 * @example
 * ```typescript
 * const sampleEn = {
 *   foo: {
 *     bar: 'Hello world',
 *     baz: 'Hello {{name}}',
 *     quux: 'Hello {{name}} {{surname}}',
 *   },
 * }
 * const st = new Translator(sampleEn, ...).st()
 *
 * const bar = buildLocalizableMessage(st.foo.bar)
 * const baz = buildLocalizableMessage(st.foo.baz, { name: 'Waldo' })
 * const quux = buildLocalizableMessage(st.foo.quux, { name: 'Waldo', surname: 'Doe' })
 *
 * bar('en') // 'Hello world'
 * baz('fr') // 'Bonjour Waldo'
 * quux('de') // 'Hallo Waldo Doe'
 * ```
 */

export type LocalizableMessage = (locale: string) => string

// overload 1: first argument is just a string, no translationOptions
export function buildLocalizableMessage(fn: string): (locale: string) => string

// overload 2: first argument is a function with no translationOptions, which can optionally accept an empty object or undefined as its first argument
export function buildLocalizableMessage<
  TFunc extends (translationOptions: Record<string, never>, locale: string) => string,
>(fn: TFunc, translationOptions?: Record<string, never> | undefined): (locale: string) => string

// overload 3: first argument is a function with `undefined` translationOptions (support for default LocalizeFunction), which can optionally accept an empty object or undefined as its first argument
export function buildLocalizableMessage<TFunc extends (translationOptions: undefined, locale: string) => string>(
  fn: undefined extends Parameters<TFunc>[0] ? TFunc : never,
  translationOptions?: Record<string, never> | undefined,
): (locale: string) => string

// overload 4: first argument is a function with some translationOptions, which must accept those options as its first argument
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildLocalizableMessage<TFunc extends (translationOptions: any, locale: string) => string>(
  fn: Record<string, never> extends Parameters<TFunc>[0] ? never : TFunc,
  translationOptions: Parameters<TFunc>[0],
): (locale: string) => string

// implementation
export function buildLocalizableMessage<TFunc extends (translationOptions: unknown, locale: string) => string>(
  fn: TFunc | string,
  translationOptions?: Parameters<TFunc>[0] | undefined,
): (locale: string) => string {
  if (typeof fn === 'string') {
    return (_locale: string) => fn
  }
  return (locale: string) => fn(translationOptions ?? {}, locale)
}
